import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import { useEffect } from 'react';
import {
  useDappCategories,
  type UseDappCategoriesResult,
} from '../../hooks/useDappCategories';
import { Button } from '../ui/button';

interface DappCategorySectionProps {
  dappId: string;
  /** Editable (admin / capable moderator) vs read-only (publisher). */
  canEdit: boolean;
  onStoreStatusChange?: (status: 'published' | 'unpublished' | null) => void;
}

/**
 * Shows a dapp's current dApp Store category and, when editing is allowed and
 * the app has been ingested by the store, lets the viewer assign a category.
 * Reused by the admin/moderator dapp detail page and the read-only publisher
 * details page.
 */
export function DappCategorySection({
  dappId,
  canEdit,
  onStoreStatusChange,
}: DappCategorySectionProps) {
  const state = useDappCategories(dappId, { canEdit });

  useEffect(() => {
    if (state.info) {
      onStoreStatusChange?.(state.info.storeStatus);
    }
  }, [state.info, onStoreStatusChange]);

  return <DappCategoryControl canEdit={canEdit} {...state} />;
}

/** The category display + assign control, for callers that own the hook. */
export function DappCategoryControl({
  canEdit,
  info,
  isLoading,
  error,
  availableCategories,
  categoriesLoading,
  categoriesError,
  selectedCategoryId,
  setSelectedCategoryId,
  isSaving,
  handleAssign,
}: UseDappCategoriesResult & { canEdit: boolean }) {
  const currentCategories = info?.categories ?? [];
  const storeIngested = info?.storeIngested ?? false;
  const currentCategoryId =
    availableCategories.find((category) =>
      currentCategories.includes(category.name),
    )?.id ?? '';

  const categoryPlaceholder = categoriesLoading
    ? 'Loading…'
    : categoriesError
      ? 'Failed to load categories'
      : availableCategories.length === 0
        ? 'No categories available'
        : 'Select category';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {currentCategories.length > 0 ? (
          currentCategories.map((category) => (
            <span
              key={category}
              className="rounded-full border border-white/5 bg-white/[0.06] px-2.5 py-0.5 text-xs font-medium text-neutral-200"
            >
              {category}
            </span>
          ))
        ) : (
          <span className="text-sm text-neutral-500">
            {isLoading ? 'Loading…' : 'Not assigned'}
          </span>
        )}
      </div>

      {!isLoading && !storeIngested && !error && (
        <p className="text-xs text-neutral-500">
          This app hasn’t been ingested by the dApp Store yet. Categories can be
          assigned once it appears in the store.
        </p>
      )}

      {canEdit && storeIngested && (
        <div className="flex items-center gap-1.5">
          <div className="relative min-w-0 flex-1">
            <select
              value={selectedCategoryId}
              onChange={(event) => setSelectedCategoryId(event.target.value)}
              disabled={isSaving || availableCategories.length === 0}
              className="h-8 w-full appearance-none rounded-lg border border-white/10 bg-black/20 pl-2.5 pr-7 text-xs text-white/90 transition-colors hover:border-white/20 focus:border-teal-500/60 focus:outline-none focus:ring-2 focus:ring-teal-500/15 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ colorScheme: 'dark' }}
            >
              <option value="">{categoryPlaceholder}</option>
              {availableCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <ExpandMoreIcon className="pointer-events-none absolute right-1.5 top-1/2 size-3.5 -translate-y-1/2 fill-current text-neutral-500" />
          </div>
          <Button
            type="button"
            size="sm"
            className="h-8 shrink-0 rounded-lg border border-teal-400/30 bg-teal-500/15 px-3 text-xs font-medium text-teal-200 transition-colors hover:border-teal-400/50 hover:bg-teal-500/25 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={
              !selectedCategoryId ||
              selectedCategoryId === currentCategoryId ||
              isSaving
            }
            onClick={() => {
              void handleAssign();
            }}
          >
            {isSaving ? '…' : 'Assign'}
          </Button>
        </div>
      )}

      {error && <div className="text-xs text-rose-300">{error}</div>}
    </div>
  );
}
